/**
 * Demo/preview visitor page with hardcoded data.
 * Access at /visit/demo to see the visitor page layout without API.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const DEMO_PROPERTY = {
  id: 1,
  name: 'Parkside Tower A',
  address: '123 Park Ave',
  city: 'New York',
  state: 'NY',
  zip: '10001',
  phone: '(555) 111-9999',
  logo_url: '',
  status: 'active',
};

const DEMO_SETTINGS = {
  directory_display: 'tenant_name' as const,
  greeting_message: 'Welcome to Parkside Tower A! Please select the unit you\'re visiting.',
  display_style: 'modern_dark' as const,
  accent_color: '#4f46e5',
  show_phone: true,
  allow_video_call: true,
  allow_phone_call: false,
  telnyx_number: undefined,
};

const DEMO_UNITS = [
  { id: 1, unit_number: '101', floor: '1', property_id: 1, display_name: '', status: 'occupied', tenant_name: 'Jane & John Doe', has_intercom: true },
  { id: 2, unit_number: '102', floor: '1', property_id: 1, display_name: '', status: 'occupied', tenant_name: 'Bob Wilson', has_intercom: true },
  { id: 3, unit_number: '103', floor: '1', property_id: 1, display_name: '', status: 'vacant', tenant_name: null, has_intercom: false },
  { id: 4, unit_number: '201', floor: '2', property_id: 1, display_name: '', status: 'occupied', tenant_name: 'Alice Brown', has_intercom: true },
  { id: 5, unit_number: '202', floor: '2', property_id: 1, display_name: '', status: 'occupied', tenant_name: 'Carlos Rivera', has_intercom: true },
  { id: 6, unit_number: '203', floor: '2', property_id: 1, display_name: '', status: 'occupied', tenant_name: 'Sarah Kim', has_intercom: true },
  { id: 7, unit_number: '301', floor: '3', property_id: 1, display_name: '', status: 'occupied', tenant_name: 'Michael Chen', has_intercom: true },
  { id: 8, unit_number: '302', floor: '3', property_id: 1, display_name: '', status: 'vacant', tenant_name: null, has_intercom: false },
  { id: 9, unit_number: '303', floor: '3', property_id: 1, display_name: '', status: 'occupied', tenant_name: 'Emily Davis', has_intercom: true },
  { id: 10, unit_number: 'PH1', floor: 'PH', property_id: 1, display_name: '', status: 'occupied', tenant_name: 'Robert & Lisa Park', has_intercom: true },
];

type ViewMode = 'directory' | 'search';

export const VisitorDemo: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('directory');

  const property = DEMO_PROPERTY;
  const settings = DEMO_SETTINGS;
  const units = DEMO_UNITS;
  const accent = settings.accent_color;

  const filteredUnits = units
    .filter((u) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        u.unit_number.toLowerCase().includes(q) ||
        (u.tenant_name && u.tenant_name.toLowerCase().includes(q)) ||
        (u.floor && u.floor.toLowerCase().includes(q))
      );
    })
    .sort((a, b) => String(a.unit_number).localeCompare(String(b.unit_number), undefined, { numeric: true }));

  const unitsByFloor = filteredUnits.reduce<Record<string, typeof units>>((acc, u) => {
    const floor = u.floor || 'Other';
    if (!acc[floor]) acc[floor] = [];
    acc[floor].push(u);
    return acc;
  }, {});

  const floors = Object.keys(unitsByFloor).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  const getLabel = (unit: typeof units[0]): string | null => {
    const mode: string = settings.directory_display;
    if (mode === 'unit_only') return null;
    if (mode === 'display_name') return unit.display_name || null;
    return unit.tenant_name || unit.display_name || null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 flex flex-col">
      {/* Header */}
      <header className="pt-8 pb-4 px-6 text-center">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg"
          style={{ backgroundColor: accent, boxShadow: `0 10px 25px -5px ${accent}40` }}
        >
          <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-white">{property.name}</h1>
        <p className="text-sm text-gray-400 mt-1">
          {property.address}, {property.city}, {property.state} {property.zip}
        </p>
        <p className="text-sm text-gray-400 mt-3 max-w-sm mx-auto">{settings.greeting_message}</p>
      </header>

      {/* Search */}
      <div className="px-6 mb-4">
        <div className="max-w-md mx-auto relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by unit, name, or floor..."
            className="w-full pl-10 pr-4 py-3 rounded-xl border bg-white/10 text-white placeholder-gray-500 border-white/10 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          )}
        </div>
      </div>

      {/* Count + view toggle */}
      <div className="px-6 mb-2">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <span className="text-xs uppercase tracking-wider text-gray-500">
            {filteredUnits.length} Unit{filteredUnits.length !== 1 ? 's' : ''}{search ? ' found' : ''}
          </span>
          {floors.length > 1 && (
            <div className="flex gap-1">
              <button onClick={() => setViewMode('directory')} className={`px-2 py-1 rounded text-xs font-medium transition-colors ${viewMode === 'directory' ? 'bg-indigo-600/30 text-white' : 'text-gray-500'}`}>By Floor</button>
              <button onClick={() => setViewMode('search')} className={`px-2 py-1 rounded text-xs font-medium transition-colors ${viewMode === 'search' ? 'bg-indigo-600/30 text-white' : 'text-gray-500'}`}>All</button>
            </div>
          )}
        </div>
      </div>

      {/* Unit Directory */}
      <main className="flex-1 px-6 pb-8">
        <div className="max-w-md mx-auto">
          {viewMode === 'directory' && floors.length > 1 ? (
            <div className="space-y-4">
              {floors.map((floor) => (
                <div key={floor} className="rounded-2xl border overflow-hidden bg-white/10 backdrop-blur-sm border-white/10">
                  <div className="px-4 py-2.5 border-b border-white/10">
                    <span className="text-xs uppercase tracking-wider font-medium text-gray-400">
                      {floor === 'PH' ? 'Penthouse' : `Floor ${floor}`}
                    </span>
                  </div>
                  <div className="divide-y divide-white/10">
                    {unitsByFloor[floor].map((unit) => (
                      <UnitRow key={unit.id} unit={unit} label={getLabel(unit)} accent={accent} allowVideoCall={settings.allow_video_call} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border overflow-hidden bg-white/10 backdrop-blur-sm border-white/10">
              <div className="px-4 py-2.5 border-b border-white/10">
                <span className="text-xs uppercase tracking-wider font-medium text-gray-400">Unit Directory</span>
              </div>
              <div className="divide-y divide-white/10">
                {filteredUnits.map((unit) => (
                  <UnitRow key={unit.id} unit={unit} label={getLabel(unit)} accent={accent} allowVideoCall={settings.allow_video_call} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Phone */}
      {settings.show_phone && property.phone && (
        <div className="text-center pb-4">
          <p className="text-xs text-gray-500 mb-1">Need help? Call the front desk</p>
          <a href={`tel:${property.phone}`} className="text-sm font-medium hover:underline" style={{ color: accent }}>{property.phone}</a>
        </div>
      )}

      {/* Footer */}
      <footer className="pb-6 text-center">
        <p className="text-xs text-gray-600">Powered by <span className="text-gray-500 font-medium">Intrlock</span></p>
      </footer>
    </div>
  );
};

const UnitRow: React.FC<{ unit: any; label: string | null; accent: string; allowVideoCall: boolean }> = ({ unit, label, accent, allowVideoCall }) => (
  <div className="flex items-center justify-between px-4 py-3 hover:bg-white/20 transition-all group">
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${accent}20` }}>
        <span className="text-sm font-bold text-white">{unit.unit_number}</span>
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-white">Unit {unit.unit_number}</p>
        {label && <p className="text-xs text-gray-400 truncate">{label}</p>}
        {!unit.tenant_name && <p className="text-xs text-gray-600 italic">Vacant</p>}
      </div>
    </div>
    {allowVideoCall && unit.tenant_name && (
      <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-medium opacity-70 group-hover:opacity-100 transition-all" style={{ backgroundColor: accent }}>
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
        Call
      </button>
    )}
  </div>
);

export default VisitorDemo;
